import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { DeliveryDateBasis } from '../../delivery-date/db/DeliveryDateBasis.js';
import { SapValidationReport } from '../../export/db/SapValidation.js';
import {
  COMPANY_CORRECTION_FIELDS,
  COMPANY_FIELD_LABELS,
  CONTACT_CORRECTION_FIELDS,
  ExtractedCompanyInfo,
} from './Company.js';
import {
  ExtractedProductInfo,
  ManualCorrectedProductInfo,
  PRODUCT_BOOKKEEPING_FIELDS,
  PRODUCT_FIELD_LABELS,
} from './ExtractedProductInfo.js';
import { diffFields, type FieldChange } from './field-changes.js';

// Two questions are folded into one rating: was the *extraction* right, and did a human have to
// touch the result? They come apart in two real cases, which is why the last two members exist:
// a correct extraction can still need a business adjustment (no stock, customer specifics), and a
// wrong result can be unfixable because the inquiry itself asks for something impossible. Both are
// deliberately kept out of the extraction's error rate — see evaluationRates in MailProcessingStats.
//
// Declaration order is the display order everywhere (see MAIL_PROCESSING_EVALUATION_OPTIONS).
export enum MailProcessingEvaluation {
  CORRECT = 'Korrekt',
  CORRECT_AFTER_CORRECTION = 'Korrekt nach Korrektur',
  CORRECT_WITH_ADJUSTMENT = 'Korrekt mit Anpassung',
  FALSE = 'Inkorrekt',
  UNSOLVABLE_INQUIRY = 'Nicht lösbar (Anfrage)',
}

// Display wordings — decoupled from the enum values, which are persisted verbatim.
export const MAIL_PROCESSING_EVALUATION_LABELS: Record<MailProcessingEvaluation, string> = {
  [MailProcessingEvaluation.CORRECT]: 'Korrekt ohne Eingriff',
  [MailProcessingEvaluation.CORRECT_AFTER_CORRECTION]: 'Korrekt mit Eingriff',
  [MailProcessingEvaluation.CORRECT_WITH_ADJUSTMENT]: 'Korrekt, fachlich angepasst',
  [MailProcessingEvaluation.FALSE]: 'Inkorrekt',
  [MailProcessingEvaluation.UNSOLVABLE_INQUIRY]: 'Nicht lösbar (Kundenseite)',
};

// Shown as a hint next to each option: the two "Korrekt …" ratings and the two "falsch" ratings are
// only distinguishable by *why* the result looks the way it does, so the wording has to carry that
// reason — otherwise they get used interchangeably and the statistics blur.
export const MAIL_PROCESSING_EVALUATION_DESCRIPTIONS: Record<MailProcessingEvaluation, string> = {
  [MailProcessingEvaluation.CORRECT]: 'Extraktion war vollständig richtig und musste nicht angefasst werden.',
  [MailProcessingEvaluation.CORRECT_AFTER_CORRECTION]:
    'Extraktion hatte Fehler; nach manueller Korrektur ist das Angebot richtig.',
  [MailProcessingEvaluation.CORRECT_WITH_ADJUSTMENT]:
    'Extraktion war richtig — angepasst wurde aus fachlichen Gründen (kundenspezifisches Wissen, kein Bestand …).',
  [MailProcessingEvaluation.FALSE]:
    'Extraktion war falsch — der Fehler liegt in der Verarbeitung, nicht in der Anfrage.',
  [MailProcessingEvaluation.UNSOLVABLE_INQUIRY]:
    'Ergebnis ist falsch, aber nicht lösbar — die Anfrage selbst trägt den Fehler (z. B. nicht existierende Produkte).',
};

// One colour per rating, used for the dot in the detail dropdown, the mailbox filter pills, the
// request list and the statistics tiles. Kept next to the labels so the call sites cannot drift.
export const MAIL_PROCESSING_EVALUATION_DOTS: Record<MailProcessingEvaluation, string> = {
  [MailProcessingEvaluation.CORRECT]: '#23a35e',
  [MailProcessingEvaluation.CORRECT_AFTER_CORRECTION]: '#009ee1',
  [MailProcessingEvaluation.CORRECT_WITH_ADJUSTMENT]: '#0f9b8e',
  [MailProcessingEvaluation.FALSE]: '#d9543f',
  // Violet rather than another red/amber shade: "nicht lösbar" is not a severity on the
  // korrekt↔falsch axis, it is a case taken out of that scale entirely.
  [MailProcessingEvaluation.UNSOLVABLE_INQUIRY]: '#8a63d2',
};

export const MAIL_PROCESSING_NOT_EVALUATED_LABEL = 'Nicht bewertet';
export const MAIL_PROCESSING_NOT_EVALUATED_DOT = '#c4c4cc';

export interface MailProcessingEvaluationOption {
  value: MailProcessingEvaluation;
  label: string;
  description: string;
  dot: string;
}

/** Every rating with its wording and colour, in display order — the list the UI renders from. */
export const MAIL_PROCESSING_EVALUATION_OPTIONS: MailProcessingEvaluationOption[] = Object.values(
  MailProcessingEvaluation,
).map(value => ({
  value,
  label: MAIL_PROCESSING_EVALUATION_LABELS[value],
  description: MAIL_PROCESSING_EVALUATION_DESCRIPTIONS[value],
  dot: MAIL_PROCESSING_EVALUATION_DOTS[value],
}));

@Entity()
export class MailExtractionResult extends AdornisEntity {
  static override _class = 'MailExtractionResult';

  // REF_1: Referenzkennzeichen
  // Nur für interne Zwecke des Kunden erfasst
  @Field(() => String) requestID: Maybe<string>;

  getRequestID() {
    return this.requestID ?? 'Anfrage';
  }

  // Wunschlieferdatum, wie es der Kunde in der Anfrage genannt hat — falls er eines genannt hat.
  // Seit dem Routenplaner NICHT mehr das, was als REQ_DATE_H nach SAP geht: viele Kunden-ERPs
  // drucken an dieser Stelle ihr Belegdatum statt eines Termins, und ein Termin, den H&D nicht
  // halten kann, hilft niemandem. Es bleibt erhalten, weil es in der Anfrage steht.
  @Field(() => DateTime) desiredDeliveryDate: Maybe<DateTime>;

  /**
   * REQ_DATE_H: das Lieferdatum, das H&D laut Routenfahrplan halten kann — `yyyy-MM-dd`.
   *
   * Bewusst ein String und kein `DateTime`: der Server läuft in UTC, und der BaseQL-Scalar liest
   * mit `DateTime.fromISO(value)` ohne `setZone`. Ein als Mitternacht Berlin gespeicherter
   * Zeitpunkt formatiert sich nach dem Weg durch die Datenbank als der **Vortag**. Ein
   * Datum ohne Uhrzeit kann nicht verrutschen.
   */
  @Field(() => String) plannedDeliveryDate: Maybe<string>;

  /**
   * Ein von Hand gesetztes Lieferdatum (`yyyy-MM-dd`). Schlägt die Berechnung und wird von einer
   * erneuten Extraktion nie überschrieben — dasselbe Prinzip wie bei Firma und Ansprechpartner:
   * was ein Mensch entschieden hat, überschreibt die KI nicht.
   */
  @Field(() => String) manualDeliveryDate: Maybe<string>;

  /** Wie `plannedDeliveryDate` zustande kam — Zone, Abfahrtstag, Transportzeit. */
  @Field(() => DeliveryDateBasis) deliveryDateBasis: Maybe<DeliveryDateBasis>;

  // Kontaktperson oder ähnliche Referenz
  @Field(() => String) reference: Maybe<string>;

  /**
   * The requesting company as it stands — the extraction's answer until someone corrects it,
   * and the corrected value from then on. Everything downstream (SAP export, validation, the
   * mailbox filter) reads exactly this field, which is why a correction is written into it
   * rather than layered beside it the way a position correction is.
   */
  @Field(() => ExtractedCompanyInfo) requestingCompany: Maybe<ExtractedCompanyInfo>;

  /**
   * What the extraction originally produced, frozen the first time a person corrects the
   * header (see {@link AbstractMailExtraction.correctCompany}). Null while nothing was
   * corrected. This is what makes a Firma-/Ansprechpartner-Korrektur visible at all — the
   * counterpart to `extractedProducts` for the header.
   */
  @Field(() => ExtractedCompanyInfo) extractedCompany: Maybe<ExtractedCompanyInfo>;

  @Field(() => [ExtractedProductInfo]) extractedProducts: Maybe<ExtractedProductInfo[]>;
  @Field(() => [ManualCorrectedProductInfo]) correctedProducts: Maybe<ManualCorrectedProductInfo[]>;

  @Field(() => Boolean) notAnOrderDocument: Maybe<boolean>;

  /**
   * Names of the attachments this run could not read — no adapter for the type, or the file
   * itself is broken. Null when everything was read.
   *
   * A property of *this* run, not of the request: a later extraction whose attachment was
   * converted simply does not carry it any more, and the question it raised closes itself.
   */
  @Field(() => [String]) unreadableAttachments: Maybe<string[]>;

  // Canonical document (step 1.5): a faithful, human-readable Markdown transcript
  // of the whole inquiry (header, positions, remarks). It is the normalized
  // intermediate that downstream structured extraction reads, and can be
  // downloaded in the frontend to debug "was everything read correctly?".
  @Field(() => String) canonicalDocument: Maybe<string>;

  constructor(doc: PartialEntityData<MailExtractionResult>) {
    super(doc);
  }
}

/** Which part of the request a correction is about. */
export type CorrectionKind = 'company' | 'contact' | 'product';

/** What happened to it. Only a position can be added or removed; the header is always there. */
export type CorrectionStatus = 'changed' | 'added' | 'removed';

/** One corrected thing, ready to be listed. */
export interface Correction {
  /** Stable within a request: 'company', 'contact', or the correction's own id. */
  key: string;
  kind: CorrectionKind;
  status: CorrectionStatus;
  /** 'Firma', 'Ansprechpartner', 'Position 10'. */
  title: string;
  /** What it is, in the user's terms — the firm's name, the contact, the material. */
  subtitle: Maybe<string>;
  /** Empty for an added or removed position: there is no before to compare against. */
  changes: FieldChange[];
}

/** Positions read in the order they appear in the offer; anything unnumbered goes last. */
const sortByPositionNumber = <T extends ExtractedProductInfo>(products: T[]): T[] => {
  const order = (product: T) => {
    const parsed = Number.parseInt(product.positionNumber ?? '', 10);
    return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
  };
  return [...products].sort((a, b) => order(a) - order(b));
};

// Shared base for anything that owns a `MailExtractionResult` and lets the user
// review/correct it: the live `MailProcessing` (mail pipeline) and the frozen
// `TestSetEntry` (regression snapshot). It bundles the extraction result, evaluation,
// notes and the product-correction behaviour so both concrete entities (and the shared
// review UI) reuse exactly one implementation.
//
// It must be `@Entity()`-decorated (not just a plain abstract class): baseql's
// registerEntity requires an entity's direct superclass to itself be a registered entity
// node — an unregistered intermediate would throw "Superclass ... is not present in the
// dependency tree". It stays `abstract` and defines no `_collectionName` of its own; no
// document ever carries its `_class`, so it is only a shared parent in the entity tree
// (same shape as MailEntity → EnrichedMailEntity). Fields/methods are inherited by the
// concrete subclasses down the prototype chain.
@Entity()
export abstract class AbstractMailExtraction extends MongoEntity {
  static override _class = 'AbstractMailExtraction';

  @Field(() => MailExtractionResult)
  extractionResult?: Maybe<MailExtractionResult>;

  @Field(() => [String])
  notes?: Maybe<string[]>;

  updateNotes() {
    return {
      add: (note: string) => {
        if (!this.notes) {
          this.notes = [];
        }
        this.notes.push(note);
      },
      delete: (index: number) => {
        this.notes?.splice(index, 1);
      },
    };
  }

  @Field(() => String)
  evaluation?: Maybe<MailProcessingEvaluation>;

  constructor(doc: PartialEntityData<AbstractMailExtraction>) {
    super(doc);
  }

  // add new correction instance for given extracted product _id
  async correctProduct(productInfo: ExtractedProductInfo): Promise<string> {
    if (!this.extractionResult) throw new Error("Can't correct product without extraction result");
    if (productInfo instanceof ManualCorrectedProductInfo) return productInfo._id;
    this.extractionResult.correctedProducts = this._ensureCorrectedProducts();
    const existingCorrection = this.extractionResult.correctedProducts?.find(
      c => c.extractedProductInfoId === productInfo._id,
    );
    if (existingCorrection) return existingCorrection._id;
    const extractedProduct = this.extractionResult.extractedProducts?.find(p => p._id === productInfo._id);
    if (!extractedProduct) throw new Error('Extracted product not found in ExtractionResult');
    return await this._createCorrectProduct({
      additionalInfo: extractedProduct.toJSON(),
      extractedProductInfoId: productInfo._id,
      save: false,
    });
  }

  async createProduct() {
    return await this._createCorrectProduct();
  }

  private async _createCorrectProduct({
    additionalInfo,
    extractedProductInfoId,
    save = true,
  }: { additionalInfo?: Record<string, any>; extractedProductInfoId?: string; save?: boolean } = {}) {
    if (!this.extractionResult) throw new Error("Can't create product without extraction result");
    this.extractionResult.correctedProducts = this._ensureCorrectedProducts();
    const id = A.getGloballyUniqueID();
    const product = new ManualCorrectedProductInfo({ ...additionalInfo, extractedProductInfoId, _id: id });
    this.extractionResult.correctedProducts?.push(product);
    if (save) await this.save();
    return id;
  }

  private _ensureCorrectedProducts() {
    if (!this.extractionResult) throw new Error("Can't ensure corrected products without extraction result");
    if (!this.extractionResult.correctedProducts) {
      this.extractionResult.correctedProducts = [];
    }
    return this.extractionResult.correctedProducts;
  }

  removeCorrection(correctedProductId: string) {
    if (!this.extractionResult?.correctedProducts) return;
    const idx = this.extractionResult.correctedProducts.findIndex(p => p._id === correctedProductId);
    if (idx >= 0) {
      this.extractionResult.correctedProducts.splice(idx, 1);
    }
  }

  private findCorrectionFor(productInfo: ExtractedProductInfo): Maybe<ManualCorrectedProductInfo> {
    if (productInfo instanceof ManualCorrectedProductInfo) return productInfo;
    return this.extractionResult?.correctedProducts?.find(c => c.extractedProductInfoId === productInfo._id);
  }

  isProductRemoved(productInfo: ExtractedProductInfo): boolean {
    return !!this.findCorrectionFor(productInfo)?.removed;
  }

  // Remove a position from the offer by flagging its correction `removed` (creating a
  // correction for a plain extracted product on demand). Kept reversible for both extracted
  // and manually added products via restoreProduct.
  async removeProduct(productInfo: ExtractedProductInfo): Promise<void> {
    if (!this.extractionResult) throw new Error("Can't remove product without extraction result");
    const correctionId = await this.correctProduct(productInfo);
    const correction = this.extractionResult.correctedProducts?.find(c => c._id === correctionId);
    if (correction) correction.removed = true;
    await this.save();
  }

  async restoreProduct(productInfo: ExtractedProductInfo): Promise<void> {
    if (!this.extractionResult) throw new Error("Can't restore product without extraction result");
    const correction = this.findCorrectionFor(productInfo);
    if (!correction) return;
    correction.removed = false;
    // Drop a correction that only existed to mark the removal so the extracted row reverts
    // cleanly — but never for a manually added product, which lives solely in the correction.
    if (correction.extractedProductInfoId && this.getProductChanges(correction).length === 0)
      this.removeCorrection(correction._id);
    await this.save();
  }

  // check if changes occur
  isProductCorrected(productInfo: ExtractedProductInfo): boolean {
    if (!this.extractionResult) throw new Error("Can't check corrected product without extraction result");
    if (productInfo instanceof ManualCorrectedProductInfo && !productInfo.extractedProductInfoId) return true;
    return this.getProductChanges(productInfo).length > 0;
  }

  // list the fields that differ between an extracted product and its correction, so the
  // UI can show *what* was changed (not just *that* it was corrected) for easier debugging.
  getProductChanges(productInfo: ExtractedProductInfo): FieldChange[] {
    if (!this.extractionResult) return [];
    const correction =
      productInfo instanceof ManualCorrectedProductInfo
        ? productInfo
        : this.extractionResult.correctedProducts?.find(c => c.extractedProductInfoId === productInfo._id);
    if (!correction?.extractedProductInfoId) return [];
    const extractedProduct = this.extractionResult.extractedProducts?.find(
      p => p._id === correction.extractedProductInfoId,
    );
    if (!extractedProduct) return [];

    const keys = Object.keys(extractedProduct.toJSON()).filter(key => !PRODUCT_BOOKKEEPING_FIELDS.has(key));
    return diffFields(extractedProduct, correction, keys, PRODUCT_FIELD_LABELS);
  }

  getProductStatus(productInfo: ExtractedProductInfo) {
    if (productInfo instanceof ManualCorrectedProductInfo) {
      if (!productInfo.extractedProductInfoId) return 'Neu erstellt';
      if (this.isProductCorrected(productInfo)) return 'Korrigiert';
    }
    return 'Extrahiert';
  }

  // ---- header corrections (Firma / Ansprechpartner) ----------------------------------
  //
  // A position keeps its extracted original beside the correction; the header cannot, because
  // half the application reads `requestingCompany` directly (XML export, SAP validation, the
  // mailbox filter's Mongo paths). So the correction is written into that field and the
  // *original* is what gets moved aside — the mirror image of the position model, with the
  // same result: nothing a person changed is ever lost.

  /**
   * The company to write a header correction into. Freezes what the extraction produced on the
   * first call, so the second and every later correction still diff against the AI original
   * rather than against the previous correction.
   *
   * Every path that changes the requesting company goes through here — the review UI and an
   * answered Rückfrage alike.
   */
  correctCompany(): ExtractedCompanyInfo {
    if (!this.extractionResult) throw new Error("Can't correct company without extraction result");
    // A mail whose company could not be read at all is exactly the case the "Keine anfragende
    // Firma erkannt" question exists for — so there has to be something to correct.
    this.extractionResult.requestingCompany ??= new ExtractedCompanyInfo({});
    this.extractionResult.extractedCompany ??= new ExtractedCompanyInfo(
      this.extractionResult.requestingCompany.toJSON(),
    );
    return this.extractionResult.requestingCompany;
  }

  private getHeaderChanges(fields: readonly string[]): FieldChange[] {
    const extracted = this.extractionResult?.extractedCompany;
    const current = this.extractionResult?.requestingCompany;
    if (!extracted || !current) return [];
    return diffFields(extracted, current, fields, COMPANY_FIELD_LABELS);
  }

  /** What was changed about the firm itself — debitor, name, address … */
  getCompanyChanges(): FieldChange[] {
    return this.getHeaderChanges(COMPANY_CORRECTION_FIELDS);
  }

  /** What was changed about the Ansprechpartner. */
  getContactChanges(): FieldChange[] {
    return this.getHeaderChanges(CONTACT_CORRECTION_FIELDS);
  }

  isCompanyCorrected(): boolean {
    return this.getCompanyChanges().length > 0 || this.getContactChanges().length > 0;
  }

  /**
   * Put the extracted original back, in memory. This is what "Abbrechen" needs: it undoes a
   * correction that was never saved, without writing anything.
   */
  discardCompanyCorrection(): void {
    const extracted = this.extractionResult?.extractedCompany;
    if (!this.extractionResult || !extracted) return;
    this.extractionResult.requestingCompany = new ExtractedCompanyInfo(extracted.toJSON());
    this.extractionResult.extractedCompany = null;
  }

  /** Discard the header correction and persist the extracted original. */
  async revertCompanyCorrection(): Promise<void> {
    if (!this.extractionResult?.extractedCompany) return;
    this.discardCompanyCorrection();
    await this.save();
  }

  // ---- everything that was corrected, in one list ------------------------------------

  /**
   * Every correction on this request — the firm, the Ansprechpartner and each position that
   * was changed, added or removed.
   *
   * The single source the review UI reads, so "wurde hier etwas angefasst?" has exactly one
   * answer no matter which part of the request it is asked about.
   */
  getCorrections(): Correction[] {
    if (!this.extractionResult) return [];
    const company = this.extractionResult.requestingCompany;

    const header: Correction[] = [];
    const companyChanges = this.getCompanyChanges();
    if (companyChanges.length)
      header.push({
        key: 'company',
        kind: 'company',
        status: 'changed',
        title: 'Firma',
        subtitle: company?.name ?? null,
        changes: companyChanges,
      });

    const contactChanges = this.getContactChanges();
    if (contactChanges.length)
      header.push({
        key: 'contact',
        kind: 'contact',
        status: 'changed',
        title: 'Ansprechpartner',
        subtitle: company?.partnerName ?? company?.contactPersonName ?? null,
        changes: contactChanges,
      });

    const positions: Correction[] = [];
    for (const correction of sortByPositionNumber(this.extractionResult.correctedProducts ?? [])) {
      // Added and then removed again: that position never reached the offer, so there is
      // nothing about it worth reporting.
      if (correction.removed && correction.isNewProduct) continue;
      const status: CorrectionStatus = correction.removed ? 'removed' : correction.isNewProduct ? 'added' : 'changed';
      // A removed position leaves the offer entirely — which of its fields differ is noise.
      const changes = status === 'changed' ? this.getProductChanges(correction) : [];
      // A correction that only ever existed to carry a removal flag that was since undone has
      // nothing left to show.
      if (status === 'changed' && !changes.length) continue;
      positions.push({
        key: correction._id,
        kind: 'product',
        status,
        title: correction.positionNumber ? `Position ${correction.positionNumber}` : 'Position ohne Nummer',
        subtitle: [correction.material, correction.productNumber].filter(Boolean).join(' · ') || null,
        changes,
      });
    }

    return [...header, ...positions];
  }
}

@Entity()
export class MailProcessing extends AbstractMailExtraction {
  static override _class = 'MailProcessing';
  static override _collectionName = 'mail-processings';
  static _versionCollection = MailProcessing._collectionName + '_versions';

  @validate(nonOptional())
  @Field(() => String)
  mailID!: string;

  @Field(() => String)
  sapNumber?: Maybe<string>;

  @Field(() => [String])
  sentToSap?: Maybe<string[]>;

  updateSentToSap() {
    return {
      add: (message: string) => {
        if (!this.sentToSap) {
          this.sentToSap = [];
        }
        this.sentToSap.push(message);
      },
    };
  }

  // Result of the last mandatory-field validation for SAP export. Persisted so the
  // mail list can show/filter blocked offers; the detail view recomputes it live.
  @Field(() => SapValidationReport)
  sapValidation?: Maybe<SapValidationReport>;

  @Field(() => Date)
  validatedAt?: Maybe<Date>;

  @Field(() => Date, { default: (v: any) => v ?? new Date() })
  createdAt: Maybe<Date>;

  constructor(doc: PartialEntityData<MailProcessing>) {
    super(doc);
  }
}
